import { ulid } from 'ulid';

import type {
	CreateParkingSessionInput,
	ChangeParkingSessionInput,
	ParkingSessionStatus
} from '@/graphql/generated';

export interface ParkingSessionFormSubmitData {
	parkingSessionId?: string;
	plateNumber: string;
	ticketId?: string | null;
	vehicleId?: string;
	contractId?: string | null;
	status?: ParkingSessionStatus;
}

/**
 * Generates an idempotent parking session ID using ULID.
 * Should be called once per form render via useMemo.
 */
export const generateIdempotentParkingSessionId = (): string => ulid();

/**
 * Builds the input payload for creating a new parking session.
 */
export const buildCreateParkingSessionInput = (
	formData: ParkingSessionFormSubmitData,
	idempotentParkingSessionId: string
): CreateParkingSessionInput => {
	return {
		parkingSessionId: idempotentParkingSessionId,
		plateNumber: formData.plateNumber,
		ticketId: formData.ticketId || undefined,
		contractId: formData.contractId || undefined
	};
};

/**
 * Builds the input payload for changing a parking session.
 */
export const buildChangeParkingSessionInput = (
	parkingSessionId: string,
	plateNumber: string,
	status: ParkingSessionStatus,
	contractId?: string | null
): ChangeParkingSessionInput => {
	return {
		parkingSessionId,
		plateNumber: plateNumber?.trim() || undefined,
		status,
		contractId: contractId || undefined
	};
};

/**
 * Maps parking session data to form data for edit mode.
 */
export const mapParkingSessionToFormData = (
	plateNumber?: string | null,
	vehicleId?: string | null,
	status?: ParkingSessionStatus | null,
	contractId?: string | null
): ParkingSessionFormSubmitData => {
	return {
		plateNumber: plateNumber ?? '',
		vehicleId: vehicleId ?? undefined,
		contractId: contractId ?? undefined,
		status: status ?? undefined
	};
};
